import { ChangeDetectionStrategy, Component, computed, input, model, signal } from '@angular/core';
import { UI_TEXT } from '../i18n';
import { ScFieldHover } from './field-hover.directive';

/**
 * Campo de texto del kit (reemplaza a dx-text-box). Como el de DevExtreme, avisa el valor al salir
 * del campo (`updateOn: 'change'`) o con cada tecla (`'input'`), y sólo cuando lo cambia la persona:
 * un valor puesto por la pantalla no avisa. En modo `search` muestra la lupa y, con texto, la × para
 * limpiar.
 *
 * El placeholder es una capa sobre el campo (como el de DevExtreme, con la letra de la raíz), no el
 * del navegador: el atributo `placeholder` queda en el input para el lector de pantalla.
 */
@Component({
  selector: 'sc-text-input',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [ScFieldHover],
  host: {
    class: 'sck-field sck-text',
    '[class.sck-field--readonly]': 'readOnly()',
    '[class.sck-field--disabled]': 'disabled()',
    '[class.sck-text--search]': "type() === 'search'",
    '[attr.title]': 'hint()',
  },
  template: `
    @if (type() === 'search') {
      <span
        class="material-icons sck-icon sck-field__search"
        aria-hidden="true"
        data-icon="search"
      ></span>
    }
    <div class="sck-field__control">
      <input
        class="sck-field__input"
        [type]="type() === 'search' ? 'text' : type()"
        [value]="value() ?? ''"
        [attr.maxlength]="maxLength()"
        [attr.placeholder]="placeholder() || null"
        [attr.aria-label]="ariaLabel()"
        [readOnly]="readOnly()"
        [disabled]="disabled()"
        (input)="onInput($event)"
        (change)="onChange($event)"
      />
      @if (placeholder() && !draft()) {
        <div
          class="sck-field__placeholder"
          aria-hidden="true"
          [attr.data-placeholder]="placeholder()"
        ></div>
      }
    </div>
    @if (type() === 'search' && draft()) {
      <button
        type="button"
        class="sck-field__clear"
        tabindex="-1"
        [attr.aria-label]="text.clear"
        (mousedown)="$event.preventDefault()"
        (click)="clear()"
      >
        <span class="material-icons sck-icon" aria-hidden="true" data-icon="cancel"></span>
      </button>
    }
  `,
})
export class ScTextInputComponent {
  readonly value = model<string | null>('');
  readonly type = input<'text' | 'email' | 'tel' | 'search'>('text');
  readonly placeholder = input('');
  readonly maxLength = input<number | null>(null);
  readonly readOnly = input(false);
  readonly disabled = input(false);
  readonly ariaLabel = input<string | null>(null);
  /** Ayuda al pasar el mouse (el `title` del campo, como `hint` de DevExtreme). */
  readonly hint = input<string | null>(null);
  /** Cuándo se avisa el valor: al salir del campo (como DevExtreme) o con cada tecla. */
  readonly updateOn = input<'change' | 'input'>('change');

  protected readonly text = UI_TEXT;
  /** Lo escrito, aunque aún no se avise (el placeholder y la × dependen de esto). */
  private readonly typed = signal<string | null>(null);
  protected readonly draft = computed(() => this.typed() ?? this.value() ?? '');

  protected onInput(event: Event): void {
    const text = (event.target as HTMLInputElement).value;
    this.typed.set(text);
    if (this.updateOn() === 'input') this.commit(text);
  }

  protected onChange(event: Event): void {
    if (this.updateOn() === 'change') this.commit((event.target as HTMLInputElement).value);
  }

  protected clear(): void {
    this.commit('');
  }

  private commit(text: string): void {
    this.typed.set(null);
    if (text !== (this.value() ?? '')) this.value.set(text);
  }
}
